// example for translation helper functions (pre define namspaces)
import type { TFunction } from 'i18next'

import { i18nextInstance } from '@/plugins/vuePluginI18next'

// i18next use typescript built-in "Intl" formatting (not typed from i18next framework)
type TranslationFormatOptionsCurrency = Intl.NumberFormatOptions
type TranslationFormatOptionsDate = Intl.DateTimeFormatOptions
type TranslationFormatOptionsDateTime = Intl.DateTimeFormatOptions
type TranslationFormatOptionsNumber = Intl.NumberFormatOptions
type TranslationFormatOptionsUnitDuration = Intl.NumberFormatOptions

const getCurrentLanguage = (): string => {
  return i18nextInstance.language
}

const translationFunctionDefault = (): TFunction => {
  return i18nextInstance.t
}

const translationFunctionBackend = (): TFunction<'backend'> => {
  return i18nextInstance.getFixedT(null, 'backend')
}

const translationFunctionCommon = (): TFunction<'common'> => {
  return i18nextInstance.getFixedT(null, 'common')
}

const translationFunctionSystem = (): TFunction<'system'> => {
  return i18nextInstance.getFixedT(null, 'system')
}

const translationFunctionFormatCurrency = (
  translationFunction: TFunction<'system'>,
  numberValue: number,
): string => {
  const formatOptionsCurrency: TranslationFormatOptionsCurrency = {
    currencyDisplay: 'symbol',
    style: 'currency',
  }

  return translationFunction('plainCurrency', {
    formatParams: { translationCurrencyValue: formatOptionsCurrency },
    translationCurrencyValue: numberValue,
  })
}

const translationFunctionFormatDate = (
  translationFunction: TFunction<'system'>,
  dateValue: Date,
): string => {
  const formatOptionsDate: TranslationFormatOptionsDate = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }

  return translationFunction('plainDate', {
    formatParams: { translationDateValue: formatOptionsDate },
    translationDateValue: dateValue,
  })
}
const translationFunctionFormatDateNumeric = (
  translationFunction: TFunction<'system'>,
  dateValue: Date,
): string => {
  const formatOptionsDate: TranslationFormatOptionsDate = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }

  return translationFunction('plainDate', {
    formatParams: { translationDateValue: formatOptionsDate },
    translationDateValue: dateValue,
  })
}

const translationFunctionFormatDateTime = (
  translationFunction: TFunction<'system'>,
  dateTimeValue: Date,
  formatOptions?: TranslationFormatOptionsDateTime,
): string => {
  const defaultFormatOptionsDateTime: TranslationFormatOptionsDateTime = {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: '2-digit',
    second: '2-digit',
    year: 'numeric',
  }
  const currentFormatOptionsDateTime =
    formatOptions === undefined
      ? defaultFormatOptionsDateTime
      : { ...defaultFormatOptionsDateTime, ...formatOptions }

  return translationFunction('plainDateTime', {
    formatParams: { translationDateTimeValue: currentFormatOptionsDateTime },
    translationDateTimeValue: dateTimeValue,
  })
}
const translationFunctionFormatDateTimeNoSeconds = (
  translationFunction: TFunction<'system'>,
  dateTimeValue: Date,
  formatOptions?: TranslationFormatOptionsDateTime,
): string => {
  const defaultFormatOptionsDateTime: TranslationFormatOptionsDateTime = {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }
  const currentFormatOptionsDateTime =
    formatOptions === undefined
      ? defaultFormatOptionsDateTime
      : { ...defaultFormatOptionsDateTime, ...formatOptions }

  return translationFunction('plainDateTime', {
    formatParams: { translationDateTimeValue: currentFormatOptionsDateTime },
    translationDateTimeValue: dateTimeValue,
  })
}

const translationFunctionFormatPercentage = (
  translationFunction: TFunction<'system'>,
  percentageValue?: number,
  isInputDisplayValue = true,
): string => {
  const fallbackPercentageValue = 0
  const percentageParameterValue = percentageValue ?? fallbackPercentageValue

  const defaultMaxFractionValue = 2
  const formatOptionsPercentage: TranslationFormatOptionsNumber = {
    maximumFractionDigits: defaultMaxFractionValue,
    style: 'percent',
  }

  const percentageBaseValue = 100
  const currentPercentageValue = isInputDisplayValue
    ? percentageParameterValue / percentageBaseValue
    : percentageParameterValue

  return translationFunction('plainPercentage', {
    formatParams: { translationPercentageValue: formatOptionsPercentage },
    translationPercentageValue: currentPercentageValue,
  })
}

type UnitDurationFormatKeyList = 'day' | 'hour' | 'minute' | 'month' | 'second' | 'week' | 'year'
const translationFunctionFormatUnitDuration = (
  translationFunction: TFunction<'system'>,
  unitDurationValue: number,
  unitDurationFormatKey?: UnitDurationFormatKeyList,
): string => {
  const currentFormatKeyUnitDuration: UnitDurationFormatKeyList = unitDurationFormatKey ?? 'day'
  const formatOptionsUnitDuration: TranslationFormatOptionsUnitDuration = {
    style: 'unit',
    unit: currentFormatKeyUnitDuration,
    unitDisplay: 'long',
  }

  return translationFunction('plainUnitDuration', {
    formatParams: { translationUnitDurationValue: formatOptionsUnitDuration },
    translationUnitDurationValue: unitDurationValue,
  })
}

export {
  getCurrentLanguage,
  translationFunctionBackend,
  translationFunctionCommon,
  translationFunctionDefault,
  translationFunctionFormatCurrency,
  translationFunctionFormatDate,
  translationFunctionFormatDateNumeric,
  translationFunctionFormatDateTime,
  translationFunctionFormatDateTimeNoSeconds,
  translationFunctionFormatPercentage,
  translationFunctionFormatUnitDuration,
  translationFunctionSystem,
}
